import { useEffect, useRef } from "react";
import { trackEvent } from "../services/analyticsService";

const TRACKING_DEDUPE_WINDOW_MS = 500;

const isScannerCloseTrack = (trackValue = "") => {
  const normalized = String(trackValue || "").toLowerCase();
  return normalized.includes("scanner") && normalized.includes("close");
};

/**
 * Global delegated click tracking hook for the active page.
 * Tracks only explicit [data-track] elements.
 */
const useTracking = ({ accessToken, userId, actorUserRole, getMetadata }) => {
  const lastEventRef = useRef({ signature: "", at: 0 });
  const metadataRef = useRef(getMetadata);

  useEffect(() => {
    metadataRef.current = getMetadata;
  }, [getMetadata]);

  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;

      const trackedEl = target.closest("[data-track]");
      if (!trackedEl) return;

      const routeEl = target.closest("[data-track-route]");
      const interactionType = "tracked-element";
      const interactionName = trackedEl.getAttribute("data-track") || "";

      if (isScannerCloseTrack(interactionName)) return;

      const routeFromDataAttr = routeEl?.getAttribute("data-track-route") || "";
      let destinationPath = routeFromDataAttr;

      const currentPath = window.location.pathname;

      const now = Date.now();
      const signature = `${interactionType}|${interactionName}|${destinationPath || currentPath}`;

      if (
        lastEventRef.current.signature === signature &&
        now - lastEventRef.current.at < TRACKING_DEDUPE_WINDOW_MS
      ) {
        return;
      }

      lastEventRef.current = { signature, at: now };

      const pageMetadata = metadataRef.current ? metadataRef.current() : {};
      const eventMetadata = {
        interactionType,
        interactionName,
        dataTrack: trackedEl?.getAttribute("data-track") || "",
        destinationPath,
        elementTag: (trackedEl || target).tagName || "",
        elementId: (trackedEl || target).id || "",
        pagePath: currentPath,
        ...pageMetadata,
      };

      void trackEvent({
        accessToken,
        userId,
        actorUserRole,
        metadata: eventMetadata,
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [accessToken, userId, actorUserRole]);
};

export default useTracking;
